import React, { Fragment, useEffect, useState } from 'react'
import { Box, Button, Center, Flex, Spinner } from '@chakra-ui/react'
import { useQueryClient } from 'react-query'

import { DashboardHeader, withApp } from '@/features/Layout'

import { Post } from '@/features/posts/types'
import { Meta } from '@/features/filters/types'

import { Pagination } from '@/components/shared/Pagination'
import { PostTable } from './PostTable'

import { POSTS } from '@/features/posts/constants'

import { useMeta } from '@/features/filters'
import { usePosts } from '@/features/posts/queries'
import { t } from '@/lib/helpers'

type Props = {
  posts: Post[]
  meta: Meta
}

function PostsPage(props: Props) {
  const { posts: initialPosts, meta: initialMeta } = props

  const { meta, setMeta } = useMeta(initialMeta)
  const { data: posts, isFetching } = usePosts({
    initialData: { data: initialPosts, meta: initialMeta },
    params: meta,
  })

  const queryClient = useQueryClient()

  const [isUserTriggeredFetching, setIsUserTriggeredFetching] = useState(false)

  useEffect(() => {
    if (!isFetching) {
      setIsUserTriggeredFetching(false)
    }
  }, [isFetching])

  return (
    <Fragment>
      <Box layerStyle="container">
        <DashboardHeader
          title={t('views.admin.posts.index.title')}
          addons={
            <Button
              onClick={() => {
                setIsUserTriggeredFetching(true)
                queryClient.refetchQueries([POSTS])
              }}
              isLoading={isUserTriggeredFetching}
              isDisabled={isUserTriggeredFetching}
              sx={{ h: '48px', minW: '100px' }}
            >
              {t('views.admin.posts.index.refresh')}
            </Button>
          }
        />

        <Box position="relative" sx={{ mb: '24px' }}>
          {isUserTriggeredFetching && (
            <Center
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="whiteAlpha.700"
              zIndex="1"
            >
              <Spinner size="lg" />
            </Center>
          )}

          <PostTable data={posts.data} />
        </Box>

        <Flex
          layerStyle="content"
          sx={{
            justifyContent: 'flex-end',
            opacity: isUserTriggeredFetching ? 0.5 : 1,
            pointerEvents: isUserTriggeredFetching ? 'none' : 'auto',
          }}
        >
          <Pagination
            page={meta.page}
            per={meta.per}
            total={meta.total}
            onChangePage={(page) => {
              setIsUserTriggeredFetching(true)
              setMeta('page', page)
            }}
          />
        </Flex>
      </Box>
    </Fragment>
  )
}

export default withApp(PostsPage)
